import { useEffect, useRef, useState } from 'react'
import { trace as fbtrace } from 'firebase/performance'
import { perf } from './firebase'
import { log, logFile } from './logger'
import { bootAttributes, traceAttributes } from './perfAttrs'

const moduleLoadWall = Date.now()
const moduleLoadPerf = performance.now()

export { isCold } from './perfAttrs'

log('boot', 'module load', bootAttributes)

export function recordTrace(
  name: string,
  startMs: number,
  durationMs: number,
  attrs?: Record<string, string>,
  metrics?: Record<string, number>,
) {
  logFile('perf', name, { ms: Math.round(durationMs), ...attrs, ...metrics })
  sendTrace(name, startMs, durationMs, attrs, metrics)
}

// Unlogged, for high-volume traces (Firestore latency) that would flood the boot log.
export function sendTrace(
  name: string,
  startMs: number,
  durationMs: number,
  attrs?: Record<string, string>,
  metrics?: Record<string, number>,
) {
  if (!perf) return
  const t = fbtrace(perf, name)
  t.record(startMs, Math.max(0, Math.round(durationMs)), {
    attributes: traceAttributes(attrs),
    metrics,
  })
}

let authResolvedWall: number | null = null
let authResolvedPerf: number | null = null

export function markAuthResolved(authState: 'none' | 'anonymous' | 'signed_in') {
  if (authResolvedWall !== null) return
  authResolvedWall = Date.now()
  authResolvedPerf = performance.now()
  const elapsed_ms = Math.round(authResolvedPerf - moduleLoadPerf)
  log('auth', `resolved — ${authState}`, { elapsed_ms })
  recordTrace('app_boot_to_auth', moduleLoadWall, elapsed_ms, { auth_state: authState })
}

let firstSnapshotRecorded = false

export function bootMilestones(): { authResolved: boolean; firstSnapshot: boolean } {
  return { authResolved: authResolvedWall !== null, firstSnapshot: firstSnapshotRecorded }
}

export function markFirstSnapshot() {
  if (firstSnapshotRecorded || authResolvedPerf === null || authResolvedWall === null) return
  firstSnapshotRecorded = true
  const elapsed_since_auth_ms = Math.round(performance.now() - authResolvedPerf)
  log('fs', 'first snapshot', { elapsed_since_auth_ms })
  recordTrace('auth_to_first_snapshot', authResolvedWall, elapsed_since_auth_ms)
}

// Mount timestamps for a trace, captured once. Taking them in a lazy state
// initializer (rather than during render) keeps the read out of the render body.
export function useTraceStart() {
  const [start] = useState(() => ({ wall: Date.now(), perf: performance.now() }))
  return start
}

export function usePageLoadTrace(
  name: string,
  ready: boolean,
  attrs?: Record<string, string>,
) {
  const start = useTraceStart()
  const recorded = useRef(false)
  useEffect(() => {
    if (ready && !recorded.current) {
      recorded.current = true
      recordTrace(name, start.wall, performance.now() - start.perf, attrs)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- start/attrs are captured at mount; the trace records once
  }, [ready, name])
}
